import { useCallback } from 'react';

import type { ApolloCache, ApolloLink, Reference } from '@apollo/client';
import { gql } from '@apollo/client';
import { Apollo } from 'src/gql/generated';

type RejectReminderMutationFn = Apollo.RejectReminderMutationHookResult[0];

const META_KEY: keyof Apollo.ReminderRowsResponse = 'meta';
const REMINDERS_KEY: keyof Apollo.ReminderRowsResponse = 'reminders';

const REMINDER_IDENTITY_FRAGMENT = gql`
  fragment ReminderIdentity on Reminder {
    __typename
    id
    reviewStatus
  }
`;

export function useGetReminder(
  reminderId: UUID,
  options?: Omit<NonNullable<Parameters<typeof Apollo.useGetReminderQuery>[0]>, 'variables'>
) {
  const { data, loading } = Apollo.useGetReminderQuery({
    ...options,
    variables: {
      reminderId,
    },
  });

  return { loading, reminder: data?.reminder ?? null };
}

void gql`
  query GetPendingReviewCount {
    reminders {
      meta {
        __typename
        pendingReviewCount
      }
    }
  }
`;

export function useGetPendingReviewCount(): {
  loading: boolean;
  pendingReviewCount: number;
} {
  const { data, loading } = Apollo.useGetPendingReviewCountQuery();
  return { loading, pendingReviewCount: data?.reminders?.meta?.pendingReviewCount ?? 0 };
}

export function useGetReminders(options?: Parameters<typeof Apollo.useGetRemindersQuery>[0]) {
  const { data, loading, refetch } = Apollo.useGetRemindersQuery(options);

  return {
    loading,
    meta: data?.reminders?.meta ?? null,
    refetch,
    reminders: data?.reminders?.reminders ?? [],
  };
}

export function useDeleteReminder(reminderId: UUID): {
  deleteReminder: () => Promise<ApolloLink.Result<Apollo.DeleteReminderMutation>>;
  loading: boolean;
} {
  const [deleteReminder, { loading }] = Apollo.useDeleteReminderMutation({
    variables: {
      reminderId,
    },
  });
  const safeDeleteReminder = useCallback(() => {
    return deleteReminder({
      update: cache => {
        updateRemindersCache(cache, { removeIds: new Set([reminderId]) });
      },
      variables: { reminderId },
    });
  }, [deleteReminder, reminderId]);

  return {
    deleteReminder: safeDeleteReminder,
    loading,
  };
}

export function useCreateReminder(): {
  createReminder: (
    options: Parameters<Apollo.CreateReminderMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.CreateReminderMutation>>;
  loading: boolean;
} {
  const [createReminder, { loading }] = Apollo.useCreateReminderMutation();
  const safeCreateReminder = useCallback(
    (options: Parameters<Apollo.CreateReminderMutationHookResult[0]>[0]) => {
      return createReminder({
        ...options,
        update: (cache, { data }) => {
          if (!data) return;
          const newRef = cache.writeFragment({
            data: data.createReminder,
            fragment: REMINDER_IDENTITY_FRAGMENT,
          });
          if (!newRef) return;
          updateRemindersCache(cache, {
            add: [{ ref: newRef, reminder: data.createReminder }],
          });
        },
      });
    },
    [createReminder]
  );

  return {
    createReminder: safeCreateReminder,
    loading,
  };
}

export function useUpdateReminders(): {
  loading: boolean;
  updateReminders: Apollo.UpdateRemindersMutationHookResult[0];
} {
  const [updateReminders, { loading }] = Apollo.useUpdateRemindersMutation();
  return { loading, updateReminders };
}

export function useDeleteReminders(): {
  deleteReminders: Apollo.DeleteRemindersMutationHookResult[0];
  loading: boolean;
} {
  const [deleteReminders, { loading }] = Apollo.useDeleteRemindersMutation({
    update: (cache, { data }) => {
      if (!data?.deleteReminders) return;
      updateRemindersCache(cache, { removeIds: new Set(data.deleteReminders) });
    },
  });

  return { deleteReminders, loading };
}

export function useAcceptReminderWithCacheUpdate(): {
  acceptReminder: Apollo.AcceptReminderMutationHookResult[0];
  loading: boolean;
} {
  const [acceptReminder, { loading }] = Apollo.useAcceptReminderMutation({
    update: cache => {
      updateRemindersCache(cache, { pendingReviewCountDelta: -1 });
    },
  });
  return { acceptReminder, loading };
}

/**
 * Removes the rejected reminder from the cache after successful mutation.
 */
export function useRejectReminderWithCacheUpdate(): {
  loading: boolean;
  rejectReminder: RejectReminderMutationFn;
} {
  const [rejectReminder, { loading }] = Apollo.useRejectReminderMutation({
    update: (cache, { data }) => {
      if (!data?.rejectReminder) return;
      updateRemindersCache(cache, {
        pendingReviewCountDelta: -1,
        removeIds: new Set([data.rejectReminder]),
      });
    },
  });

  return { loading, rejectReminder };
}

export function useAcceptRemindersWithCacheUpdate(): {
  acceptReminders: Apollo.AcceptRemindersMutationHookResult[0];
  loading: boolean;
} {
  const [acceptReminders, { loading }] = Apollo.useAcceptRemindersMutation({
    update: (cache, { data }) => {
      if (!data?.acceptReminders) return;
      updateRemindersCache(cache, { pendingReviewCountDelta: -data.acceptReminders.length });
    },
  });
  return { acceptReminders, loading };
}

/**
 * Removes all rejected reminders from the cache after successful mutation.
 */
export function useRejectRemindersWithCacheUpdate(): {
  loading: boolean;
  rejectReminders: Apollo.RejectRemindersMutationHookResult[0];
} {
  const [rejectReminders, { loading }] = Apollo.useRejectRemindersMutation({
    update: (cache, { data }) => {
      if (!data?.rejectReminders) return;
      updateRemindersCache(cache, {
        pendingReviewCountDelta: -data.rejectReminders.length,
        removeIds: new Set(data.rejectReminders),
      });
    },
  });

  return { loading, rejectReminders };
}

export function useRemindersSubscription() {
  return Apollo.useOnRemindersUpdatedSubscription({
    onData: ({ client, data: subscriptionData }) => {
      const event = subscriptionData.data?.remindersUpdated;
      if (!event?.reminders.length) return;

      const addEntries = event.reminders.flatMap(r => {
        const ref = client.cache.writeFragment({
          data: r,
          /**
           * Update cache function below requires additional fields like reviewStatus/completedAt
           * to update the cache correctly.
           */
          fragment: gql`
            fragment NewReminder on Reminder {
              __typename
              id
              completedAt
              reviewStatus
            }
          `,
        });
        return ref ? [{ ref, reminder: r }] : [];
      });

      updateRemindersCache(client.cache, {
        add: addEntries,
        meta: event.meta,
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Shared cache helpers
// ---------------------------------------------------------------------------

type ReminderFilterInfo = Pick<
  Apollo.ReminderSimpleFragment,
  'completedAt' | 'id' | 'reviewStatus'
>;

interface RemindersCacheUpdate {
  add?: { ref: Reference; reminder: ReminderFilterInfo }[];
  meta?: Pick<Apollo.ReminderMeta, 'pendingReviewCount'>;
  pendingReviewCountDelta?: number;
  removeIds?: Set<string>;
}

/**
 * Single entry-point for all optimistic cache updates to the `reminders` query.
 *
 * Apollo calls the modifier once per cached variant (e.g. Incomplete, Completed,
 * PendingReview). After removing deleted refs and merging new ones, ALL refs are
 * re-filtered against the variant's filter by reading their current fields from
 * the normalized cache. This means status changes (e.g. accept) automatically
 * evict reminders from variants they no longer match.
 */
function updateRemindersCache(cache: ApolloCache, update: RemindersCacheUpdate) {
  const { add = [], pendingReviewCountDelta = 0, removeIds = new Set() } = update;

  cache.modify({
    fields: {
      reminders: (existing: Reference, { readField, storeFieldName }) => {
        const filter = parseFilterFromStoreFieldName(storeFieldName);
        const meta = readField<Apollo.ReminderMeta>(META_KEY, existing);
        const existingRefs = readField<Reference[]>(REMINDERS_KEY, existing) ?? [];
        const refsAfterRemoval = removeIds.size
          ? existingRefs.filter(ref => !removeIds.has(readField<UUID>('id', ref) ?? ''))
          : existingRefs;
        const refsAfterRemovalIdSet = new Set(
          refsAfterRemoval.map(ref => readField<UUID>('id', ref))
        );
        const newRefs = add
          .filter(({ reminder }) => !refsAfterRemovalIdSet.has(reminder.id))
          .map(({ ref }) => ref);
        const refsMatchingFilter = [...refsAfterRemoval, ...newRefs].filter(ref => {
          const completedAt = readField<string | null>(
            'completedAt' satisfies keyof Apollo.Reminder,
            ref
          );
          const reviewStatus = readField<Apollo.ReminderReviewStatus>(
            'reviewStatus' satisfies keyof Apollo.Reminder,
            ref
          );
          return reminderMatchesFilter(
            {
              completedAt: completedAt ?? null,
              reviewStatus: reviewStatus ?? Apollo.ReminderReviewStatus.Accepted,
            },
            filter
          );
        });
        const pendingReviewCount = update.meta
          ? update.meta.pendingReviewCount
          : (meta?.pendingReviewCount ?? 0) + pendingReviewCountDelta;

        return buildUpdatedResponse(existing, refsMatchingFilter, {
          ...meta,
          pendingReviewCount: Math.max(pendingReviewCount, 0),
          totalCount: refsMatchingFilter.length,
        });
      },
    },
  });
}

function buildUpdatedResponse(
  existing: Reference,
  reminders: Reference[],
  meta: Partial<Apollo.ReminderMeta> | undefined
) {
  return {
    ...existing,
    __typename: 'ReminderRowsResponse' as const,
    meta: { ...meta, __typename: 'ReminderMeta' as const },
    reminders,
  };
}

function parseFilterFromStoreFieldName(storeFieldName: string) {
  const filterKey: keyof Apollo.GetRemindersQueryVariables = 'filter';
  const match = storeFieldName.match(new RegExp(`"${filterKey}":"(\\w+)"`));
  if (!match?.[1]) return null;

  const filterValues = Object.values(Apollo.ReminderFilter);
  const value = filterValues.find(v => v === match[1]);

  return value ?? null;
}

function reminderMatchesFilter(
  reminder: Pick<Apollo.Reminder, 'completedAt' | 'reviewStatus'>,
  filter: Apollo.ReminderFilter | null
) {
  switch (filter) {
    case Apollo.ReminderFilter.Completed:
      return Boolean(
        reminder.reviewStatus !== Apollo.ReminderReviewStatus.PendingReview && reminder.completedAt
      );
    case Apollo.ReminderFilter.Incomplete:
      return Boolean(
        reminder.reviewStatus !== Apollo.ReminderReviewStatus.PendingReview && !reminder.completedAt
      );
    case Apollo.ReminderFilter.PendingReview:
      return (
        reminder.reviewStatus === Apollo.ReminderReviewStatus.PendingReview && !reminder.completedAt
      );
    case Apollo.ReminderFilter.All:
    default:
      return true;
  }
}
